import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Upload,
  Image as ImageIcon,
  Video,
  Trash2,
  Star,
  RefreshCw,
  AlertCircle,
  CheckCircle
} from "lucide-react";
import { propertyMediaService, PropertyMedia, UploadMediaRequest } from "@/services/propertyMediaService";
import { useToast } from "@/hooks/use-toast";

interface PropertyMediaManagerProps {
  propertyId: string;
  onMediaUploaded?: () => void;
}

export function PropertyMediaManager({ propertyId, onMediaUploaded }: PropertyMediaManagerProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [formData, setFormData] = useState({
    alt_text: "",
    title: "",
    description: "",
    is_primary: false
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [imageBlobUrls, setImageBlobUrls] = useState<Record<string, string>>({});
  const [imagesLoading, setImagesLoading] = useState(false);

  // Fetch existing media for the property
  const { data: mediaResponse, isLoading: mediaLoading, refetch: refetchMedia } = useQuery({
    queryKey: ['propertyMedia', propertyId],
    queryFn: () => propertyMediaService.getPropertyMedia(parseInt(propertyId)),
    enabled: !!propertyId,
    staleTime: 30000,
  });

  // Debug the API response structure
  console.log('🔧 PropertyMediaManager mediaResponse:', mediaResponse);
  console.log('🔧 PropertyMediaManager mediaResponse.data:', mediaResponse?.data);
  console.log('🔧 PropertyMediaManager mediaResponse.data type:', typeof mediaResponse?.data);
  console.log('🔧 PropertyMediaManager mediaResponse.data isArray:', Array.isArray(mediaResponse?.data));
  
  // Safely extract media array with multiple fallbacks
  const existingMedia = Array.isArray(mediaResponse?.data) 
    ? mediaResponse.data 
    : Array.isArray((mediaResponse?.data as any)?.data)
    ? (mediaResponse?.data as any).data
    : Array.isArray((mediaResponse?.data as any)?.media)
    ? (mediaResponse?.data as any).media
    : [];
    
  console.log('🔧 PropertyMediaManager existingMedia:', existingMedia);
  console.log('🔧 PropertyMediaManager existingMedia length:', existingMedia.length);

  // Helper function to fetch image as blob with authentication
  const fetchImageAsBlob = async (url: string, mediaId: number): Promise<string | null> => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.warn('No auth token available for image fetch');
        return null;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error(`Failed to fetch image: ${response.status} ${response.statusText}`);
        return null;
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      return blobUrl;
    } catch (error) {
      console.error('Error fetching image as blob:', error);
      return null;
    }
  };

  // Fetch all images as blobs when existingMedia changes
  useEffect(() => {
    if (!existingMedia || existingMedia.length === 0) {
      setImagesLoading(false);
      return;
    }

    const fetchAllImages = async () => {
      setImagesLoading(true);
      
      // Clean up old blob URLs first
      setImageBlobUrls(prev => {
        Object.values(prev).forEach(url => URL.revokeObjectURL(url));
        return {};
      });
      
      for (const mediaItem of existingMedia) {
        if (mediaItem.media_type === 'image' && mediaItem.url) {
          // Normalize URL first
          let imageUrl = mediaItem.url;
          if (!imageUrl.startsWith('http')) {
            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
            imageUrl = imageUrl.startsWith('/') ? `${apiBaseUrl}${imageUrl}` : `${apiBaseUrl}/${imageUrl}`;
          }

          const blobUrl = await fetchImageAsBlob(imageUrl, mediaItem.id);
          if (blobUrl) {
            // Update state incrementally so images appear as they load
            setImageBlobUrls(prev => ({ ...prev, [mediaItem.id.toString()]: blobUrl }));
          }
        }
      }

      setImagesLoading(false);
    };

    fetchAllImages();

    // Cleanup function
    return () => {
      setImageBlobUrls(prev => {
        Object.values(prev).forEach(url => URL.revokeObjectURL(url));
        return {};
      });
      setImagesLoading(false);
    };
  }, [existingMedia]);

  // Upload media mutation
  const uploadMediaMutation = useMutation({
    mutationFn: (data: UploadMediaRequest) => 
      propertyMediaService.uploadMedia(parseInt(propertyId), data),
    onSuccess: (response) => {
      // Double check that it's actually successful
      if (response.success === false) {
        // This shouldn't happen if error handling is correct, but just in case
        throw new Error(response.message || 'Upload failed');
      }
      
      queryClient.invalidateQueries({ queryKey: ['propertyMedia', propertyId] });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      refetchMedia(); // Refresh media list
      resetForm();
      toast({
        title: t("success"),
        description: t("mediaUploadedSuccessfully") || "Media uploaded successfully",
      });
      if (onMediaUploaded) {
        onMediaUploaded();
      }
    },
    onError: (error: any) => {
      console.error('🔧 Upload media mutation error:', error);
      
      let errorMessage = t("failedToUploadMedia") || "Failed to upload media";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.originalError?.message) {
        errorMessage = error.response.data.originalError.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: t("error"),
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Delete media mutation
  const deleteMediaMutation = useMutation({
    mutationFn: (mediaId: number) => 
      propertyMediaService.deleteMedia(parseInt(propertyId), mediaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propertyMedia', propertyId] });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      toast({
        title: t("success"),
        description: t("mediaDeletedSuccessfully") || "Media deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: t("error"),
        description: t("failedToDeleteMedia") || "Failed to delete media",
        variant: "destructive",
      });
    },
  });

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    Array.from(files).forEach((file) => {
      const isValidType = file.type.startsWith('image/') || file.type.startsWith('video/');
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB

      if (!isValidType) {
        invalidFiles.push(`${file.name}: Invalid file type`);
      } else if (!isValidSize) {
        invalidFiles.push(`${file.name}: File too large (max 10MB)`);
      } else {
        validFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      toast({
        title: t("error"),
        description: invalidFiles.join(', '),
        variant: "destructive",
      });
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      
      // Auto-fill title if empty and only one file
      if (validFiles.length === 1 && !formData.title) {
        setFormData(prev => ({
          ...prev,
          title: validFiles[0].name.split('.')[0]
        }));
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      alt_text: "",
      title: "",
      description: "",
      is_primary: false
    });
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedFiles.length === 0) {
      toast({
        title: t("error"),
        description: t("pleaseSelectFile") || "Please select files to upload",
        variant: "destructive",
      });
      return;
    }

    if (!formData.alt_text.trim() || !formData.title.trim()) {
      toast({
        title: t("error"),
        description: t("pleaseCompleteRequiredFields") || "Please complete all required fields",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    
    try {
      // Upload files one by one
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const uploadData: UploadMediaRequest = {
          media: file,
          alt_text: formData.alt_text,
          title: selectedFiles.length === 1 ? formData.title : `${formData.title} ${i + 1}`,
          description: formData.description,
          is_primary: formData.is_primary && i === 0 // Only first file can be primary
        };

        await new Promise((resolve, reject) => {
          uploadMediaMutation.mutate(uploadData, {
            onSuccess: resolve,
            onError: reject
          });
        });
      }
      
      // Reset form after all uploads complete
      resetForm();
      
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMedia = (media: PropertyMedia) => {
    if (window.confirm(t("confirmDeleteMedia") || "Are you sure you want to delete this media?")) {
      deleteMediaMutation.mutate(media.id);
    }
  };


  return (
    <div className="space-y-6">
      {/* Upload Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            {t("uploadNewMedia")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* File Upload Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={(e) => handleFileSelect(e.target.files)}
                className="hidden"
              />
              
              {selectedFiles.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                    <span className="text-sm font-medium">
                      {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                    </span>
                  </div>
                  
                  <div className="max-h-32 overflow-y-auto space-y-2">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                        <div className="flex items-center gap-2">
                          {file.type.startsWith('image/') ? (
                            <ImageIcon className="h-4 w-4 text-green-600" />
                          ) : (
                            <Video className="h-4 w-4 text-blue-600" />
                          )}
                          <div>
                            <p className="text-xs font-medium truncate max-w-32">{file.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSelectedFile(index)}
                          className="h-6 w-6 p-0"
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedFiles([])}
                  >
                    Clear All
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-12 w-12 text-muted-foreground mx-auto" />
                  <div>
                    <p className="text-sm font-medium">{t("dropFilesHere")}</p>
                    <p className="text-xs text-muted-foreground">{t("orClickToSelect")}</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {t("selectFile")}
                  </Button>
                </div>
              )}
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="alt_text">{t("altText")} *</Label>
                <Input
                  id="alt_text"
                  value={formData.alt_text}
                  onChange={(e) => handleInputChange("alt_text", e.target.value)}
                  placeholder={t("enterAltText")}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title">{t("title")} *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  placeholder={t("enterTitle")}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t("description")}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                placeholder={t("enterDescription")}
                rows={3}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_primary"
                checked={formData.is_primary}
                onCheckedChange={(checked) => handleInputChange("is_primary", checked)}
              />
              <Label htmlFor="is_primary">{t("setPrimaryImage")}</Label>
            </div>

            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={uploading || uploadMediaMutation.isPending}
              >
                {uploading || uploadMediaMutation.isPending ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    {t("uploading")}
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    {t("uploadMedia")}
                  </>
                )}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm}>
                {t("reset")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Existing Media */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            {t("existingMedia")} ({existingMedia.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {mediaLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              {t("loading")}...
            </div>
          ) : !Array.isArray(existingMedia) || existingMedia.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-2" />
              <p>{t("noMediaFound")}</p>
              {!Array.isArray(existingMedia) && (
                <p className="text-xs mt-2 text-red-500">
                  Debug: existingMedia is not an array (type: {typeof existingMedia})
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.isArray(existingMedia) && existingMedia.map((media) => {
                const blobUrl = imageBlobUrls[media.id.toString()];
                const isImage = media.media_type === 'image';
                const isLoading = isImage && !blobUrl && imagesLoading;
                
                return (
                  <div key={media.id} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                      {isLoading ? (
                        <div className="w-full h-full flex items-center justify-center">
                          <RefreshCw className="h-8 w-8 text-muted-foreground animate-spin" />
                        </div>
                      ) : isImage && blobUrl ? (
                        <img
                          src={blobUrl}
                          alt={media.alt_text}
                          className="w-full h-full object-cover"
                          onLoad={() => console.log('✅ Image loaded successfully:', blobUrl)}
                          onError={(e) => {
                            console.error('❌ Image failed to load:', blobUrl);
                          }}
                        />
                      ) : isImage ? (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="h-12 w-12 text-muted-foreground opacity-50" />
                        </div>
                      ) : (
                        <video
                          src={blobUrl || media.url}
                          className="w-full h-full object-cover"
                          controls
                        />
                      )}
                    
                    {media.is_primary && (
                      <div className="absolute top-2 left-2">
                        <Badge className="bg-yellow-500 text-yellow-50">
                          <Star className="h-3 w-3 mr-1" />
                          {t("primary")}
                        </Badge>
                      </div>
                    )}
                    
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteMedia(media)}
                        disabled={deleteMediaMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="mt-2 space-y-1">
                    <p className="text-sm font-medium truncate">{media.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{media.alt_text}</p>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
